import type { ReactNode } from 'react';
import { Box, Flex, Grid, Text, chakra } from '@chakra-ui/react';
import { ChevronLeft, MoreHorizontal } from 'lucide-react';
import { ProfileDropdown } from '@/components/ui/ProfileDropdown/ProfileDropdown';
import { MarkAllReadButton } from '@/components/common/NotificationRow';

interface NotificationsHeaderProps {
  newCount: number;
  showMore: boolean;
  onBack: () => void;
  onMore: () => void;
  onMarkAllRead: () => Promise<unknown>;
}

function CircleButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <chakra.button
      type="button"
      aria-label={label}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w={{ base: '36px', lg: '40px' }}
      h={{ base: '36px', lg: '40px' }}
      border="none"
      borderRadius="full"
      bg="white"
      color="volyx.text"
      boxShadow="0 1px 3px rgba(31, 41, 50, 0.08)"
      cursor="pointer"
      transition="box-shadow 0.15s ease"
      _hover={{ boxShadow: '0 2px 6px rgba(31, 41, 50, 0.14)' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'volyx.action', outlineOffset: '2px' }}
      onClick={onClick}
    >
      {children}
    </chakra.button>
  );
}

/**
 * Mobile: back + "Mark all as read" + "···" on one row, large title below. Desktop: title
 * left, "Mark all as read", "···" and the profile menu right; the sidebar replaces the back
 * button. "Mark all as read" is only there while something is unread.
 */
export function NotificationsHeader({
  newCount,
  showMore,
  onBack,
  onMore,
  onMarkAllRead,
}: NotificationsHeaderProps) {
  return (
    <Grid
      as="header"
      templateColumns="minmax(0, 1fr) auto"
      templateAreas={{ base: '"back more" "title title"', lg: '"title more"' }}
      alignItems="center"
      rowGap="3.5"
      columnGap="4"
    >
      <Box gridArea="back" hideFrom="lg">
        <CircleButton label="Back" onClick={onBack}>
          <ChevronLeft size={20} aria-hidden />
        </CircleButton>
      </Box>
      <Flex gridArea="title" align="center" gap="2.5" minW="0">
        <Text
          as="h1"
          m="0"
          fontSize={{ base: '32px', lg: '36px' }}
          lineHeight={{ base: '38px', lg: '44px' }}
          fontWeight="bold"
          letterSpacing="-0.015em"
          color="volyx.text"
        >
          Notifications
        </Text>
        {newCount > 0 && (
          <Box
            as="span"
            flexShrink={0}
            px="2"
            py="0.5"
            borderRadius="full"
            bg="volyx.action"
            color="white"
            fontSize="13px"
            lineHeight="18px"
            fontWeight="semibold"
          >
            {newCount} new
          </Box>
        )}
      </Flex>
      <Flex gridArea="more" justifySelf="end" align="center" gap="3">
        {newCount > 0 && (
          // Matches the white circle buttons beside it on the gray page.
          <MarkAllReadButton
            onMarkAll={onMarkAllRead}
            h={{ base: '36px', lg: '40px' }}
            bg="white"
            boxShadow="0 1px 3px rgba(31, 41, 50, 0.08)"
            _hover={{ boxShadow: '0 2px 6px rgba(31, 41, 50, 0.14)' }}
          />
        )}
        {showMore && (
          <CircleButton label="More options" onClick={onMore}>
            <MoreHorizontal size={20} aria-hidden />
          </CircleButton>
        )}
        <Box hideBelow="lg">
          <ProfileDropdown />
        </Box>
      </Flex>
    </Grid>
  );
}
